import { ArrowRight, CreditCard } from 'lucide-react';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { Button } from '@/code/frontend/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/code/frontend/components/ui/dialog';
import { useToast } from '@/code/frontend/hooks/use-toast';
import { LinkRouter } from '@/code/lib/routes';
import { trpc } from '@/code/lib/utils/trpc';

// Create a completely separate non-dismissible subscription dialog
function ForceSubscriptionDialog({
  isOpen,
}: {
  isOpen: boolean;
}): JSX.Element | null {
  // Only render when open
  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} modal={true}>
      <DialogContent
        hideCloseButton
        className="sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Subscription Required</DialogTitle>
          <DialogDescription>
            You need an active subscription to continue using this feature.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-4">
          <p className="text-sm text-gray-500">
            Your subscription has expired or is inactive. Please renew your
            subscription to regain access to all features.
          </p>
          <div className="flex justify-center">
            <Button asChild className="w-full">
              <Link
                href={'mailto:sales@aurium.ai'}
                className="inline-flex items-center gap-2"
              >
                <CreditCard className="h-4 w-4" />
                Contact Us
              </Link>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function Notifications(): JSX.Element {
  const notifications = trpc.notifications.getNotifications.useQuery();
  const acceptTerms = trpc.notifications.acceptTerms.useMutation();

  // State for subscription dialog
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);

  const { toast } = useToast();

  // biome-ignore lint/correctness/useExhaustiveDependencies: Skip
  useEffect(() => {
    if (!!notifications.data?.shouldShowTerms) {
      const termsId = notifications.data?.shouldShowTerms.id;
      const { dismiss } = toast({
        title: 'Our Terms of Service Have Been Updated',
        description: (
          <div className="space-y-4 pt-2">
            <div className="flex gap-3">
              <Button variant="outline" size="sm" asChild>
                <Link
                  href={LinkRouter.routeForTerms()}
                  className="inline-flex items-center gap-2"
                >
                  Read Terms
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button
                size="sm"
                onClick={async () => {
                  await acceptTerms.mutateAsync({
                    termsId,
                  });
                  dismiss(); // works because of javascript closure magic
                }}
              >
                I Accept
              </Button>
            </div>
          </div>
        ),
        duration: 0, // Won't dismiss automatically
      });
    }
    // we are not showing this because it's really annoying and you can find it via the user button.
    // if (notifications.data?.shouldShowLostLinkedinAccess) {
    //   // this is pretty major, we need to show a popup or something
    //   reauthLinkedinDialog.open(
    //     {
    //       forceConnect: true,
    //       title: `We need to reconfirm your connection to LinkedIn`,
    //     },
    //     { width: 'screen-lg' },
    //   );
    // }
    if (notifications.data?.shouldShowNoSubscriptionMessage) {
      // Show forced subscription dialog - can't be closed
      setShowSubscriptionDialog(true);
    }
  }, [notifications.data, toast]);

  return (
    <>
      {/* Render the subscription dialog directly */}
      <ForceSubscriptionDialog isOpen={showSubscriptionDialog} />
    </>
  );
}
